import { useCallback } from 'react';
import { useMutation } from '@tanstack/react-query';
import { useToast } from '@cad-arc';
import type { SensorAlertBundle, SensorsResource } from '@mark43/cad-api';
import { useApiClient } from '~/api-client';
import { useSensorsStore } from './use-sensors-store';

export type LinkSensorAlert = (args: {
    alert: SensorAlertBundle;
    cadEventId: number;
    isLinked: boolean;
}) => void;

/**
 * Links a sensor alert to a CAD event, or unlinks it.
 *
 * Both directions hit the same shape of endpoint and both report failure synchronously, which is
 * why this does not need optimistic state: the server publishes the whole updated alert on the
 * stream, and the store upserts it by `alertKey`. So the UI updates from the stream, not from a
 * local guess — and the toast listener stays quiet because the key is already held.
 *
 * `missingAlertKeys` in the response means the alert aged out before the link landed. Alerts live
 * roughly 48 hours, and can go earlier under memory pressure, so there is genuinely nothing to
 * link to. A partial batch still links the rest, so this reports what failed rather than treating
 * the whole call as failed.
 */
export function useLinkSensorAlerts(): LinkSensorAlert {
    const apiClient = useApiClient();
    const toast = useToast();
    const sensorsStore = useSensorsStore();

    const link = useMutation(
        function setAlertsLinked(variables: {
            alertKeys: string[];
            cadEventId: number;
            isLinked: boolean;
        }) {
            return apiClient.request<SensorsResource.LinkSensorAlerts | SensorsResource.UnlinkSensorAlerts>({
                method: 'POST',
                url: variables.isLinked ? 'sensors/alerts/link' : 'sensors/alerts/unlink',
                data: { alertKeys: variables.alertKeys, eventId: variables.cadEventId },
            });
        },
        {
            onSuccess(response, variables) {
                const result = response.data.data;
                if (!result) {
                    return;
                }
                /*
                 * The stream delivers the updated alert too, but that is a separate round trip
                 * through pubsub; applying the response here is what makes the panel change on the
                 * click. Reconciled rather than upserted because `alerts` is the event's whole
                 * linked set — see `reconcileEventLinks` for why upserting broke unlink.
                 */
                sensorsStore.reconcileEventLinks(variables.cadEventId, result.alerts);

                if (result.missingAlertKeys.length > 0) {
                    toast({
                        status: 'error',
                        description: `${result.missingAlertKeys.length} sensor event${
                            result.missingAlertKeys.length === 1 ? '' : 's'
                        } could not be ${
                            variables.isLinked ? 'linked' : 'unlinked'
                        } — no longer in recent history.`,
                    });
                }
            },
        }
    );

    return useCallback(
        function linkSensorAlert({
            alert,
            cadEventId,
            isLinked,
        }: {
            alert: SensorAlertBundle;
            cadEventId: number;
            isLinked: boolean;
        }) {
            link.mutate({ alertKeys: [alert.alertKey], cadEventId, isLinked });
        },
        [link]
    );
}

/** The CAD events an alert is currently linked to. */
export function getLinkedEventIds(alert: SensorAlertBundle): number[] {
    return alert.linkedEvents.map((link) => link.eventId);
}
